import { Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import PaymentsIcon from "@mui/icons-material/Payments";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { formatCurrency, formatInteger } from "../formatters";
import { PerformanceRow } from "./performanceApiMapping";

const PRIMARY = "#8E24AA";
const POSITIVE = "#2E7D32";
const NEGATIVE = "#C62828";

function sum(rows: PerformanceRow[], field: keyof PerformanceRow): number {
  return rows.reduce((total, row) => total + (row[field] as number), 0);
}

interface PerformanceKpiCardsProps {
  rows: PerformanceRow[];
}

export default function PerformanceKpiCards({ rows }: PerformanceKpiCardsProps) {
  const impressions = sum(rows, "impressions");
  const clicks = sum(rows, "clicks");
  const spend = sum(rows, "spend");
  const leads = sum(rows, "leads");
  const qualified = sum(rows, "qualified");
  const paid = sum(rows, "paid");
  const income = sum(rows, "income");
  const profit = income - spend;
  const cpc = clicks ? spend / clicks : 0;
  const cpq = qualified ? spend / qualified : 0;

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1 }}>
              CLICKS
            </Typography>
            <AdsClickIcon sx={{ color: PRIMARY }} />
          </Stack>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 1, fontSize: 30, color: "#1a1a1a" }}>
            {formatInteger(clicks)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9e9e9e", fontSize: 12, display: "block", mt: 0.25 }}>
            {formatInteger(impressions)} impressions - CPC {formatCurrency(cpc)}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1 }}>
              SPEND
            </Typography>
            <PaymentsIcon sx={{ color: "#E65100" }} />
          </Stack>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 1, fontSize: 30, color: "#1a1a1a" }}>
            {formatCurrency(spend)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9e9e9e", fontSize: 12, display: "block", mt: 0.25 }}>
            CPQ {formatCurrency(cpq)}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1 }}>
              LEADS
            </Typography>
            <GroupsIcon sx={{ color: PRIMARY }} />
          </Stack>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 1, fontSize: 30, color: "#1a1a1a" }}>
            {formatInteger(leads)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9e9e9e", fontSize: 12, display: "block", mt: 0.25 }}>
            {formatInteger(qualified)} qualified - {formatInteger(paid)} paid
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#ececec" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" fontWeight={600} color={PRIMARY} sx={{ flex: 1 }}>
              PROFIT
            </Typography>
            {profit >= 0 ? (
              <TrendingUpIcon sx={{ color: POSITIVE }} />
            ) : (
              <TrendingDownIcon sx={{ color: NEGATIVE }} />
            )}
          </Stack>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mt: 1, fontSize: 30, color: profit > 0 ? POSITIVE : profit < 0 ? NEGATIVE : "#1a1a1a" }}
          >
            {formatCurrency(profit)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9e9e9e", fontSize: 12, display: "block", mt: 0.25 }}>
            income {formatCurrency(income)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}
